import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import * as S from './styles'
import { useClickOutside } from '../drawer/hook'
import { ListIcon } from '@/assets/icons'
import { LogoNhWhite } from '@/assets/logos'
import { ButtonIcon, Drawer, Image } from '@/components'
import { HeaderMapper } from '@/service/api/footerHeader'
import { usePageContext } from '@/store/pageContext'
import { useScreen } from '@/store/screen'

export type HeaderProps = {
  /**
   * Background color of the header
   *
   * @default '#0708131a'
   *
   * @example
   * ```tsx
   * <Header backgroundColor="#0708131a" />
   * ```
   *
   */
  backgroundColor?: string

  /**
   * Backdrop filter of the header
   *
   * @default 'blur(10px)'
   *
   * @example
   * ```tsx
   * <Header backdropFilter="blur(10px)" />
   * ```
   *
   */
  backdropFilter?: string
  headerData: HeaderMapper
}

export type SubOptions = {
  index: number
  show: boolean
}
export const Header: React.FC<HeaderProps> = ({
  backgroundColor,
  backdropFilter,
  headerData,
}) => {
  const headerRef = useRef<HTMLDivElement>()
  const [shrinked, setShrink] = useState(false)
  const { isBiggerMobile, windowSize } = useScreen()
  const router = useRouter()
  const { darkMode } = usePageContext()

  const { open, setOpen, showSubOptions, setShowSubOptions } = useClickOutside({
    isBiggerMobile,
  })

  useEffect(() => {
    const handleScroll = () => {
      const isDocumentTopBeingManipulated = !!document.body.style.top

      if (window.scrollY >= windowSize.height / 2) {
        setShrink(true)
      } else if (!isDocumentTopBeingManipulated) setShrink(false)
    }
    window.addEventListener('scroll', handleScroll)

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [windowSize.height])

  React.useEffect(() => {
    if (open) document.body.style.overflowY = 'hidden'
    else document.body.style.overflowY = 'visible'
  }, [open])

  const navigate = useCallback(
    (path: string) => {
      if (!path.includes('https')) {
        router.push(path)
      } else window.open(path, '_blank')
      setTimeout(() => {
        setOpen(false)
        setShowSubOptions({ index: -1, show: false })
      }, 500)
    },
    [router, setOpen, setShowSubOptions],
  )

  const showSubOption = useCallback(
    (index: number) => {
      setShowSubOptions((prev) => {
        if (prev.index === index && prev.show) {
          return { index, show: false }
        }
        return { index, show: true }
      })
    },
    [setShowSubOptions],
  )
  const options = useMemo(
    () =>
      headerData.menuItens?.map((item, index) => ({
        text: item.name,
        path: item.path,
        onClick: !item.isSubOptions
          ? () => navigate(`${item.path}`)
          : () => showSubOption(index),
        subOptions: item.isSubOptions
          ? item.subOptions.map((subOption) => ({
              text: subOption.name,
              path: subOption.path,
              onClick: () => navigate(`${subOption.path}`),
            }))
          : [],
      })),
    [navigate, headerData.menuItens, showSubOption],
  )

  return (
    <S.FlexContainerHeader
      open={open}
      backgroundColor={backgroundColor}
      backdropFilter={backdropFilter}
      padding="0"
      ref={headerRef}
      id="header-container"
    >
      <S.HeaderContainer open={open} shrinked={shrinked} id="header-container">
        <Link href="/" passHref className="images">
          <Image src={LogoNhWhite} alt="Logo branca da nata.house" />
        </Link>
        {!open && (
          <ButtonIcon
            icon={ListIcon}
            alt="Botão para acessar opções do menu"
            onClick={() => setOpen(true)}
            className="drawer-toogle"
          />
        )}
      </S.HeaderContainer>
      <Drawer
        darkMode={!!darkMode}
        isBiggerMobile={isBiggerMobile}
        open={open}
        options={options}
        setOpen={setOpen}
        showSubOptions={showSubOptions}
        path={router.pathname}
      />
    </S.FlexContainerHeader>
  )
}
